import { configureStore } from '@reduxjs/toolkit';
import { PreAuthRouteDecision } from '@rapid-recovery-agency-inc/insightt-graphql';

import {
  createAuthController,
  type AuthController,
  type AuthControllerStore,
  type RenewalOutcome,
} from './auth-controller';
import {
  authReducer,
  invalidateSession,
  setCleanupStatus,
  setRenewalStatus,
  setSessionStatus,
} from './auth-slice';
import { AuthTimeoutError, PROVIDER_LOGOUT_TIMEOUT_MS, SILENT_RENEWAL_TIMEOUT_MS, withTimeout } from './bounded';
import type { AuthenticationOutcomeEvent } from './events';
import { getBoundAppAuthStore } from './app-auth-store';
import { AUTH_INITIAL_GENERATION, AUTH_SLICE_NAME } from './constants';
import { createLegacySessionAdapter, LegacySessionAdapter } from './legacy-session-adapter';
import { getSharedSsoOidcClient, markSsoSessionEndedLocally, type OidcClientDependencies } from './oidc-client';
import { validateOidcConfig } from './oidc-config';
// NOTE: `./types` is imported with an explicit `.ts` extension — this directory also has a
// legacy `types.js` (unrelated `SET_DEVICE_TOKEN_REQUEST` action type, consumed by `actions.js`).
// Vite/vitest's default module resolution order checks `.js` before `.ts`, so the extensionless
// specifier would silently resolve to `types.js` at runtime. See constants.ts for the same note.
import {
  CleanupTransactionStatus,
  DashboardSessionStatus,
  RenewalEpisodeStatus,
  type AuthState,
} from './types.ts';

/**
 * The mode-neutral public session/token/cleanup facade (T017; FR-004, FR-005, FR-007, FR-008).
 *
 * This is the ONE boundary every feature module, request client (Apollo/apisauce/RTK Query), route
 * guard, and logout/unauthorized handler is meant to consume instead of reading/writing
 * tokens/session state directly (research.md §1; constitution "centralize token/session reads and
 * writes"; FR-008 "feature modules MUST NOT branch on authentication mode"). This task (T017) only
 * *defines* the facade and an isolated legacy compatibility adapter with contract coverage — it
 * does **not** wire any real consumer onto it (that is T018/T024/T025/T027/T028/T029/T030's job).
 * `cleanup()`'s `PROVIDER_ATTEMPTED` step attempts the SSO client's discovered `end_session` via
 * the `logoutSsoSession` dependency (T036; data-model.md §9 "Explicit SSO logout then attempts
 * only discovered client-scoped `end_session`"), branching to `COMPLETE` or `PARTIAL_LOGOUT`
 * without ever restoring the local state already cleared before that attempt (FR-022).
 *
 * Token storage (FR-005, data-model.md §6 "Credential Envelope"): the SSO access/ID token and
 * expiry live ONLY in a private variable closed over by {@link createAuthBoundary} — never in
 * Redux, never in `localStorage`, never in a URL. The `auth` Redux slice (T014) continues to hold
 * only non-secret lifecycle status/generation. The legacy mechanism's own credential (the
 * `access_token` cookie) is deliberately left where it already lives today and is reached only
 * through {@link LegacySessionAdapter} — see `legacy-session-adapter.ts` for why cutting over that
 * storage mechanism itself is explicitly out of this task's scope (T029).
 *
 * Mode-neutral callers (FR-008): `getAccessToken()`, `isAuthenticated()`, and `cleanup()` never
 * require the caller to know or check whether the current session is SSO or legacy — the only
 * branch on authentication mode lives inside this module's private helpers. `getAuthMode()` exists
 * for the narrow set of call sites that must display or log *which* compatibility mode is active
 * (never to decide whether a request or route is authorized), and reuses `PreAuthRouteDecision`'s
 * `SSO`/`LEGACY` members (data-model.md §3 exact set) so mode identity in the codebase does not
 * gain a second, boundary-local enum.
 */

/** Which compatibility mode backs the current session. Reuses #3885's `PreAuthRouteDecision`. */
export type AuthMode = PreAuthRouteDecision.SSO | PreAuthRouteDecision.LEGACY;

/** Credentials published into the boundary's private in-memory SSO envelope. */
export interface SsoCredentials {
  readonly accessToken: string;
  readonly idToken?: string | null;
  /** Epoch-ms expiry, when known (drives T027's future single-flight renewal). */
  readonly expiresAt?: number | null;
}

/**
 * The private, never-Redux, never-`localStorage` SSO credential envelope (data-model.md §6).
 * Module-private shape — never exported — so nothing outside {@link createAuthBoundary} can hold a
 * reference to it.
 */
interface SsoCredentialEnvelope {
  accessToken: string | null;
  idToken: string | null;
  expiresAt: number | null;
}

const emptySsoEnvelope = (): SsoCredentialEnvelope => ({ accessToken: null, idToken: null, expiresAt: null });

/**
 * Attempts the one provider-side `end_session` call, bounded by `PROVIDER_LOGOUT_TIMEOUT_MS`
 * (T036; FR-032). Returns `true` only when `logoutSsoSession()` itself resolved in time — the real
 * implementation practically never does (see `logoutSsoSession`'s doc comment), so `false` (a
 * rejection or a timeout) is the expected outcome for a real SSO session and drives `runCleanup()`'s
 * `PARTIAL_LOGOUT` branch, never a rollback of the local clearing that already happened.
 */
const attemptProviderLogout = (logoutSsoSession: () => Promise<void>): Promise<boolean> =>
  withTimeout(logoutSsoSession(), PROVIDER_LOGOUT_TIMEOUT_MS, 'Provider logout').then(
    () => true,
    () => false,
  );

/** Dependencies {@link createAuthBoundary} composes. Mirrors T016's `createAuthController(store)` DI shape. */
export interface AuthBoundaryDependencies {
  readonly store: AuthControllerStore;
  readonly authController: AuthController;
  /** Defaults to {@link createLegacySessionAdapter}'s real cookie-backed adapter. */
  readonly legacyAdapter?: LegacySessionAdapter;
  /**
   * Performs the ONE `prompt=none` silent-renewal attempt (T027; research.md §9), or rejects when
   * the provider/library reports the attempt failed or was blocked (offline, revoked, browser-
   * blocked third-party iframe, malformed output, timeout, etc.) — {@link ensureFreshSession}
   * settles ANY rejection as `interaction_required`, never a retry.
   *
   * Deliberately `Promise<void>`, not `Promise<SsoCredentials>`: the real `oidc-spa`-backed
   * implementation (T022's `SsoOidcClient.renewTokens`, wired below by
   * {@link getAppAuthBoundary}) resolves with no value at all — verified against the compiled
   * `node_modules/oidc-spa/core/createOidc.js` (v10.2.11), `renewTokens()`'s returned promise only
   * ever settles AFTER `createSsoOidcClient()`'s own `subscribeToTokensChange` callback has already
   * published the renewed tokens into THIS SAME boundary via {@link setSsoCredentials} (see
   * `oidc-client.ts`'s `SsoOidcClient.renewTokens` doc comment for the full trace). So by the time
   * this resolves, `ensureFreshSession()` reads its own already-updated `ssoEnvelope` back out
   * instead of receiving credentials as a return value — there is nothing else for this contract to
   * hand back.
   */
  readonly renewSsoSession?: () => Promise<void>;
  /**
   * Attempts the ONE discovered client-scoped `end_session` provider termination during
   * `cleanup()`'s `PROVIDER_ATTEMPTED` step (T036; FR-021, FR-022, FR-032; data-model.md §9
   * "Explicit SSO logout then attempts only discovered client-scoped `end_session`"), or rejects
   * when that termination could not be confirmed.
   *
   * Deliberately `Promise<void>`, mirroring {@link renewSsoSession}'s shape — but unlike that
   * dependency, the real `oidc-spa`-backed implementation (T036's `createAppLogoutSsoSession`,
   * wired below by {@link getAppAuthBoundary}) practically never resolves at all on its documented
   * success path: `oidc-spa`'s real `Oidc.LoggedIn['logout']` is typed `Promise<never>` and, per
   * this task's own verification against the compiled `node_modules/oidc-spa/core/createOidc.js`
   * (v10.2.11), either performs a real top-level browser navigation (to the discovered
   * `end_session_endpoint`, or directly to the post-logout URL when none is configured) and then
   * never settles, or settles only via an anomalous, not-expected-to-happen synchronous
   * `signoutRedirect()` throw. `runCleanup()` therefore races this dependency against an explicit
   * bounded timeout (FR-032) rather than trusting it to settle on its own, and treats a timeout
   * exactly like a rejection — "provider termination could not be confirmed" — never like success.
   */
  readonly logoutSsoSession?: (postLogoutPath: string) => Promise<void>;
  /**
   * Reports renewal outcomes (FR-025). Injected rather than imported: `events.ts` loads the analytics
   * and Redux store graph, which this module must stay free of (see `shared/utils.ts`).
   */
  readonly reportOutcome?: (event: AuthenticationOutcomeEvent) => void;
  /**
   * Clears the SSO library's own browser state during local clearing, so a logout whose provider
   * termination fails is not silently undone on the next page load (FR-022).
   */
  readonly clearSsoLibraryState?: () => void;
}

/** Options for {@link AuthBoundary.cleanup}. */
export interface CleanupOptions {
  /**
   * Same-origin path the provider's `end_session` returns to after an SSO logout. Defaults to the
   * logout result screen (T065).
   */
  readonly postLogoutPath?: string;
  /**
   * Called right before the SSO provider logout starts. That logout navigates the page away, so
   * this is the last moment to queue anything, such as the terminal logout event (T104).
   */
  readonly onProviderLogoutStart?: () => void;
}

/** Where an SSO provider logout returns by default: the sign-in page. */
export const DEFAULT_POST_LOGOUT_PATH = '/user/login';

export interface AuthBoundary {
  /**
   * The current Bearer access token for request infrastructure (#3884), or `null` when neither an
   * SSO nor a legacy session is present. Never branches visibly on mode: an SSO token (when
   * present) takes precedence, otherwise the legacy adapter's token is returned unchanged.
   */
  getAccessToken(): string | null;
  /** The current SSO ID token, or `null` (no ID token concept exists for a legacy session). */
  getIdToken(): string | null;
  /** True when {@link getAccessToken} would return a non-null token. */
  isAuthenticated(): boolean;
  /** Which compatibility mode is currently active, or `null` when neither is. */
  getAuthMode(): AuthMode | null;
  /** Publishes freshly obtained SSO credentials into the private in-memory envelope. */
  setSsoCredentials(credentials: SsoCredentials): void;
  /**
   * Explicitly authorizes the legacy adapter's session to back the boundary for the CURRENT
   * Dashboard session generation (T033; FR-018, FR-019, FR-020; Key Entity "Legacy Fallback
   * Authorization" — "a policy result ... not a user preference and cannot be inferred locally").
   *
   * The legacy `access_token` cookie is written/cleared by code entirely outside this boundary
   * (the existing login page, sagas, request-client interceptors — see `legacy-session-adapter.ts`),
   * so its mere presence is never by itself sufficient to (re-)establish a session once this
   * boundary has run an authoritative `cleanup()` — see `isLegacyEstablishmentCurrentlyAuthorized`
   * below for the full rationale. A caller that just completed a genuine, policy-approved legacy
   * sign-in (a later task's concern — T034 wires the actual call site) invokes this to record that
   * establishment against the generation current at that moment, so `getAccessToken()`/
   * `getAuthMode()` resume honoring the legacy adapter for exactly that generation — and
   * automatically stop again the next time `cleanup()` invalidates it.
   *
   * Deliberately does not itself call `authController.beginSession()` — starting a new generation
   * is a decision for the caller that knows whether this is a fresh sign-in (should start one) or a
   * page-load-time detection of a still-valid pre-existing session (should not), matching how
   * `setSsoCredentials` similarly only publishes credentials without opining on session lifecycle.
   */
  establishLegacySession(): void;
  /**
   * One-attempt silent renewal and interaction-required settlement (T027; FR-032; research.md §9;
   * data-model.md §8 "IDLE → ATTEMPTING → SUCCEEDED | INTERACTIVE_REQUIRED"). Request clients
   * (Apollo/apisauce/RTK Query — T028/T029/T030) call this before/on an unauthorized response
   * instead of reading/renewing tokens themselves, and branch only on the returned
   * {@link RenewalOutcome} — never on a thrown error, and never by redirecting themselves.
   *
   * Concurrent callers share exactly ONE in-flight `prompt=none` attempt via
   * `authController.renewalEpisode` (T016's generic single-flight primitive): a second caller
   * during that attempt receives the same promise, not a second attempt, and every waiter observes
   * the same settled outcome. Once settled (either outcome) the single-flight slot clears itself,
   * so the next caller starts a genuinely fresh attempt — this method never retries on its own.
   *
   * A legacy-only (or absent) session has no `prompt=none`/silent-renewal concept — the legacy
   * cookie is either present or it is not (`legacy-session-adapter.ts`) — so this resolves
   * immediately from the boundary's already-known state without starting any episode.
   *
   * Safety against a stale settlement (no prior-scope data publication): the Dashboard session
   * generation current at the moment the attempt actually starts is captured, and re-checked once
   * the attempt settles. If a DIFFERENT generation is current by then (e.g. `cleanup()` ran
   * concurrently and invalidated the session while the sole attempt was still pending), the settled
   * result is discarded — it never publishes credentials into the envelope and never resurrects a
   * session that has since been cleared or replaced.
   */
  ensureFreshSession(): Promise<RenewalOutcome>;
  /**
   * Runs the idempotent, single-flight cleanup/logout transaction (FR-007, FR-021, FR-022,
   * FR-032; data-model.md §9): clears the private SSO envelope and the legacy adapter's storage
   * exactly once (local-first), then — only when an SSO session existed — attempts the ONE
   * discovered client-scoped `end_session` provider termination via the `logoutSsoSession`
   * dependency, bounded by an explicit timeout (T036). The local clearing already performed is
   * NEVER rolled back or restored regardless of that attempt's outcome (FR-022); the transaction
   * always ends by invalidating the Dashboard session generation, so the caller remains locally
   * signed out whether the transaction settles `COMPLETE` or `PARTIAL_LOGOUT`. Concurrent callers
   * (e.g. simultaneous unauthorized responses) share the same in-flight transaction via T016's
   * `cleanupEpisode` single-flight primitive, so local clearing and the resulting session
   * invalidation happen exactly once regardless of how many callers requested cleanup.
   */
  cleanup(options?: CleanupOptions): Promise<void>;
  /**
   * The settled status of the most recent cleanup transaction on this page (`COMPLETE` or
   * `PARTIAL_LOGOUT`), or `null` when none has settled yet (T065; FR-022).
   */
  getLastCleanupStatus(): CleanupTransactionStatus | null;
  /**
   * Registers `listener` to run whenever the session changes (credentials published, legacy
   * session established, renewal latched, cleanup settled). Returns an unsubscribe function, so
   * route guards can re-render on change instead of reading once (T082).
   */
  subscribe(listener: () => void): () => void;
}

/**
 * Creates an {@link AuthBoundary} bound to `dependencies`. Takes an explicit store/controller (the
 * same DI shape T016's `createAuthController` uses) rather than importing the app's real
 * `src/redux/store` module-level singleton, so this module stays free of that store's eager
 * side effects (browser history, sagas) and remains independently testable — a later task wires a
 * real singleton instance from the app's actual store/controller.
 */
export function createAuthBoundary(dependencies: AuthBoundaryDependencies): AuthBoundary {
  const { store, authController } = dependencies;
  const legacyAdapter = dependencies.legacyAdapter ?? createLegacySessionAdapter();

  const ssoEnvelope: SsoCredentialEnvelope = emptySsoEnvelope();

  /** The session generation the current SSO credentials belong to (T082). */
  let ssoSessionGeneration: number | null = null;

  /**
   * Set once `cleanup()` starts. A new SSO sign-in always arrives on a fresh page load (the
   * provider returns to the app root), so any SSO credentials published on this page after
   * cleanup are a stale background renewal and are ignored (T082).
   */
  let ssoPublicationClosed = false;

  /**
   * Set when a silent renewal attempt settles as interaction-required. Later callers get the same
   * outcome without a new attempt until an interactive sign-in publishes credentials (T081;
   * FR-015, FR-023).
   */
  let renewalLatched = false;

  let lastCleanupStatus: CleanupTransactionStatus | null = null;

  const listeners = new Set<() => void>();
  const notify = (): void => {
    listeners.forEach((listener) => listener());
  };

  const clearSsoEnvelope = (): void => {
    ssoEnvelope.accessToken = null;
    ssoEnvelope.idToken = null;
    ssoEnvelope.expiresAt = null;
    ssoSessionGeneration = null;
  };

  const currentGeneration = (): number => store.getState().auth.session.generation;

  const markSession = (generation: number, status: DashboardSessionStatus): void => {
    store.dispatch(setSessionStatus({ generation, status }));
  };

  /**
   * The Dashboard session generation {@link establishLegacySession} last recorded as legacy-
   * authorized, or `null` when no explicit establishment has ever been recorded by this boundary
   * instance (T033). Private, never Redux — like `ssoEnvelope`, this is boundary-internal
   * bookkeeping, not a second source of truth for the generation counter itself (`session.generation`
   * remains the one authoritative counter, owned by the `auth` slice/T016's `LatestGenerationEpisode`).
   */
  let legacyAuthorizedGeneration: number | null = null;

  /**
   * True when the legacy adapter's session may currently be trusted by {@link getAccessToken}/
   * {@link getAuthMode} to establish or continue an authenticated session (T033; FR-018, FR-019,
   * FR-020; phase goal: "never through a direct route or inferred mode").
   *
   * ## Design decision (no single explicit spec mechanism — see this task's own notes)
   *
   * The legacy `access_token` cookie predates this boundary and is still written/cleared entirely
   * outside it (`legacy-session-adapter.ts`'s doc comment). Trusting it unconditionally would let a
   * cookie that reappears or persists AFTER this boundary has already run an authoritative
   * `cleanup()` — logout, unauthorized handling, or an SSO-side sign-out — silently "downgrade" an
   * already-terminated (or already-SSO) session back into an inferred LEGACY one. So the legacy
   * fallback is honored ONLY for:
   *
   *  - **The inactive baseline**: this boundary instance has never run a cleanup/logout transaction
   *    at all (`session.generation === AUTH_INITIAL_GENERATION` and `session.status ===
   *    DashboardSessionStatus.UNKNOWN` — `runCleanup` is the only thing that ever advances either).
   *    Preserved unconditionally per FR-020 ("supported legacy UI/behavior MUST remain available
   *    while rollout policy requires it") — a page load that has never touched this boundary must
   *    behave exactly as legacy authentication always has.
   *  - **The current authoritative decision**: `legacyAuthorizedGeneration` was explicitly recorded
   *    (via {@link establishLegacySession}) for the EXACT generation the store currently reports.
   *    A recording made for a since-superseded generation (e.g. a stale/late-settling legacy
   *    sign-in attempt, or one generation back from the current `cleanup()`) is never honored —
   *    the same "only the latest generation's result may apply" discipline `ensureFreshSession`
   *    above already enforces for SSO renewal (FR-018).
   *
   * Reuses the existing `session.generation`/`session.status` fields already in the store — no
   * parallel generation-tracking mechanism is introduced; `legacyAuthorizedGeneration` only records
   * WHICH of the store's own generations was authorized, exactly like `ensureFreshSession`'s
   * `generationAtStart` local capture.
   */
  const isLegacyEstablishmentCurrentlyAuthorized = (): boolean => {
    const { generation, status } = store.getState().auth.session;
    if (generation === AUTH_INITIAL_GENERATION && status === DashboardSessionStatus.UNKNOWN) {
      return true;
    }
    return legacyAuthorizedGeneration === generation;
  };

  const getAccessToken = (): string | null => {
    if (ssoEnvelope.accessToken !== null) {
      return ssoEnvelope.accessToken;
    }
    if (!isLegacyEstablishmentCurrentlyAuthorized()) {
      return null;
    }
    return legacyAdapter.getAccessToken() ?? null;
  };

  const getAuthMode = (): AuthMode | null => {
    if (ssoEnvelope.accessToken !== null) {
      return PreAuthRouteDecision.SSO;
    }
    if (isLegacyEstablishmentCurrentlyAuthorized() && legacyAdapter.hasSession()) {
      return PreAuthRouteDecision.LEGACY;
    }
    return null;
  };

  /**
   * Skew subtracted from `expiresAt` before treating the current SSO token as still fresh enough to
   * skip a renewal attempt entirely (data-model.md §6 "`expiresAt` ... [d]rives single-flight
   * renewal using provider-approved lifetime/skew"). A Dashboard-local choice (not specified by any
   * FR) — 30s comfortably covers normal request latency without materially shrinking the token's
   * effective lifetime.
   */
  const RENEWAL_EXPIRY_SKEW_MS = 30_000;

  const hasUnexpiredSsoToken = (): boolean =>
    ssoEnvelope.accessToken !== null &&
    (ssoEnvelope.expiresAt === null || ssoEnvelope.expiresAt - RENEWAL_EXPIRY_SKEW_MS > Date.now());

  /** Settles a failed or stale attempt: the usable session is cleared until an interactive sign-in (T081). */
  const settleInteractionRequired = (generationAtStart: number): RenewalOutcome => {
    if (currentGeneration() === generationAtStart) {
      renewalLatched = true;
      // Cleanup may run after the envelope is gone. End restoration now so a reload cannot
      // silently revive a session that already requires an interactive sign-in (#4207).
      try {
        dependencies.clearSsoLibraryState?.();
      } catch {
        // Full or blocked browser storage must not retain expired credentials or stall recovery.
      }
      clearSsoEnvelope();
      store.dispatch(setRenewalStatus({ status: RenewalEpisodeStatus.INTERACTIVE_REQUIRED }));
      markSession(generationAtStart, DashboardSessionStatus.RECOVERABLE);
      notify();
    }
    return { outcome: 'interaction_required' };
  };

  const ensureFreshSession = (): Promise<RenewalOutcome> => {
    if (renewalLatched) {
      // One silent attempt per failure episode (FR-015): never start another until the user signs in.
      return Promise.resolve({ outcome: 'interaction_required' });
    }

    if (getAuthMode() !== PreAuthRouteDecision.SSO) {
      // No silent-renewal concept for a legacy or absent session (research.md §9 is SSO-only) —
      // report the boundary's already-known state instead of starting an episode.
      return Promise.resolve(
        getAccessToken() !== null ? { outcome: 'success' } : { outcome: 'interaction_required' },
      );
    }

    if (hasUnexpiredSsoToken()) {
      // Already fresh — nothing to renew, and no reason to spend the one allowed `prompt=none`
      // attempt.
      return Promise.resolve({ outcome: 'success' });
    }

    const renewSsoSession = dependencies.renewSsoSession;
    if (!renewSsoSession) {
      // No renewal capability wired (see this dependency's own doc comment): an expired-but-
      // still-present token cannot be silently refreshed, so this is the "silent renewal
      // unavailable" case.
      return Promise.resolve({ outcome: 'interaction_required' });
    }

    return authController.renewalEpisode.run(async (): Promise<RenewalOutcome> => {
      const generationAtStart = currentGeneration();
      const report = dependencies.reportOutcome ?? (() => undefined);
      store.dispatch(setRenewalStatus({ status: RenewalEpisodeStatus.ATTEMPTING }));
      markSession(generationAtStart, DashboardSessionStatus.RENEWING);
      report({ stage: 'renewal', outcome: 'started', reason: 'none', interactionType: 'automatic' });

      // Resolves void, not credentials: the real implementation has already published fresh
      // tokens into `ssoEnvelope` (through `setSsoCredentials`) by the time it settles. Bounded,
      // because a hung attempt must settle as interaction-required rather than wait forever (T057).
      const renewalError = await withTimeout(renewSsoSession(), SILENT_RENEWAL_TIMEOUT_MS, 'Silent renewal').then(
        () => null,
        (error: unknown) => error ?? new Error('Silent renewal failed'),
      );
      const renewed = renewalError === null;

      if (currentGeneration() !== generationAtStart) {
        // A different session generation became current while the attempt was in flight (e.g.
        // `cleanup()` ran concurrently): never keep credentials for a superseded session
        // (FR-032/FR-033). `setSsoCredentials` already ignores post-cleanup publications.
        return { outcome: 'interaction_required' };
      }

      if (!renewed || !hasUnexpiredSsoToken()) {
        // Rejected, timed out, or resolved without publishing a fresh token.
        report({
          stage: 'renewal',
          outcome: 'failed',
          reason: renewalError instanceof AuthTimeoutError ? 'timeout' : 'browser_blocked',
        });
        return settleInteractionRequired(generationAtStart);
      }

      report({ stage: 'renewal', outcome: 'succeeded', reason: 'none' });
      store.dispatch(setRenewalStatus({ status: RenewalEpisodeStatus.SUCCEEDED }));
      markSession(generationAtStart, DashboardSessionStatus.AUTHENTICATED);
      return { outcome: 'success' };
    });
  };

  const runCleanup = async (options: CleanupOptions = {}): Promise<void> => {
    ssoPublicationClosed = true;
    store.dispatch(setCleanupStatus({ status: CleanupTransactionStatus.REQUESTED }));
    store.dispatch(setCleanupStatus({ status: CleanupTransactionStatus.LOCAL_CLEARING }));

    // Captured BEFORE clearing below: whether an SSO session existed determines whether there is
    // a discovered client-scoped `end_session` worth attempting for `PROVIDER_ATTEMPTED` further
    // down (data-model.md §9 "Explicit SSO logout then attempts only discovered client-scoped
    // `end_session`") — a legacy-only or never-authenticated session has no provider-side
    // termination concept at all (`legacy-session-adapter.ts`'s doc comment), so there is nothing
    // to attempt at the provider level for those cases.
    const hadSsoSession = ssoEnvelope.accessToken !== null;

    // Local-first clearing (research.md §11): SSO in-memory envelope and legacy cookie storage,
    // exactly once per transaction regardless of how many concurrent callers requested cleanup.
    clearSsoEnvelope();
    renewalLatched = false;
    legacyAdapter.clear();
    if (hadSsoSession) {
      dependencies.clearSsoLibraryState?.();
    }
    // Ties legacy TERMINATION to the same transaction as the coming generation invalidation below
    // (T033): revoking the authorization recorded by `establishLegacySession` here — rather than
    // leaving it dangling until some later read happens to notice a generation mismatch — means no
    // window exists where this transaction has cleared the cookie but a previously authorized
    // generation number could still satisfy `isLegacyEstablishmentCurrentlyAuthorized` (e.g. if a
    // future caller ever re-derived the "current" generation before `invalidateSession` below has
    // dispatched). Idempotent — clearing an already-`null` authorization is a no-op, matching
    // `legacyAdapter.clear()`'s own idempotency.
    legacyAuthorizedGeneration = null;

    // Invalidates the session generation right after local clearing, before the up-to-10s provider
    // attempt, so nothing that settles during that attempt can act on the old session (T082). It
    // also returns renewal/cleanup status to their idle baseline; the transaction's own terminal
    // status is dispatched after it below and kept for `getLastCleanupStatus()` (T065).
    store.dispatch(invalidateSession());
    store.dispatch(setCleanupStatus({ status: CleanupTransactionStatus.LOCAL_CLEARED }));
    notify();

    let terminalStatus = CleanupTransactionStatus.COMPLETE;
    if (hadSsoSession && dependencies.logoutSsoSession) {
      // Only an SSO session has a discovered client-scoped `end_session` worth attempting. Local
      // state above is already cleared and is never restored regardless of the outcome (FR-022).
      store.dispatch(setCleanupStatus({ status: CleanupTransactionStatus.PROVIDER_ATTEMPTED }));
      const logoutSsoSession = dependencies.logoutSsoSession;
      const postLogoutPath = options.postLogoutPath ?? DEFAULT_POST_LOGOUT_PATH;
      options.onProviderLogoutStart?.();
      const providerLoggedOut = await attemptProviderLogout(() => logoutSsoSession(postLogoutPath));
      terminalStatus = providerLoggedOut
        ? CleanupTransactionStatus.COMPLETE
        : CleanupTransactionStatus.PARTIAL_LOGOUT;
    }
    // Legacy-only or never-authenticated sessions have no provider termination, so local clearing
    // alone completes them.
    store.dispatch(setCleanupStatus({ status: terminalStatus }));
    lastCleanupStatus = terminalStatus;
    notify();
  };

  return {
    getAccessToken,

    getIdToken: () => ssoEnvelope.idToken,

    isAuthenticated: () => getAccessToken() !== null,

    getAuthMode,

    setSsoCredentials: (credentials) => {
      if (ssoPublicationClosed) {
        return;
      }
      if (ssoSessionGeneration === null || ssoSessionGeneration !== currentGeneration()) {
        // First credentials on this page, or the previous SSO session was superseded: a new sign-in.
        ssoSessionGeneration = authController.beginSession().generation;
      }
      ssoEnvelope.accessToken = credentials.accessToken;
      ssoEnvelope.idToken = credentials.idToken ?? null;
      ssoEnvelope.expiresAt = credentials.expiresAt ?? null;
      renewalLatched = false;
      markSession(ssoSessionGeneration, DashboardSessionStatus.AUTHENTICATED);
      notify();
    },

    establishLegacySession: () => {
      // Called only after a genuine, policy-approved legacy sign-in (LoginPage), so it starts a new
      // session generation (T082).
      const { generation } = authController.beginSession();
      legacyAuthorizedGeneration = generation;
      markSession(generation, DashboardSessionStatus.AUTHENTICATED);
      notify();
    },

    ensureFreshSession,

    // `cleanupEpisode` is a shared `SingleFlightEpisode<unknown>` (T016) — its generic is erased
    // to `unknown` so other call sites needing a different result type can reuse the same slot.
    // Must return this exact promise (not a `.then()`/`async` wrapper) so concurrent `cleanup()`
    // calls are provably the same in-flight transaction (see auth-boundary.test.ts's single-
    // flight identity assertion); the cast is safe because `runCleanup` above is itself typed
    // `(): Promise<void>`, so the erased `unknown` only affects the shared class's declared type,
    // not what the promise actually resolves to.
    cleanup: (options) => authController.cleanupEpisode.run(() => runCleanup(options)) as Promise<void>,

    getLastCleanupStatus: () => lastCleanupStatus,

    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}

/**
 * The real app-wide {@link AuthBoundary} singleton (T025; FR-004, FR-042 "authorization is
 * established by trusted session/permission contracts").
 *
 * `createAuthBoundary` (above) intentionally takes an explicit store/controller instead of
 * importing the app's real `src/redux/store` module (see its own doc comment) — but T024's
 * `main.tsx`'s boot-time bootstrap and T020's `GatewayRoute`/`AuthenticationSsoHandoff`
 * both need ONE shared instance so that credentials one component publishes are visible to
 * another, and future consumers (Apollo/apisauce/RTK Query — T028/T029/T030, not this task) need a
 * single place to import it from rather than each constructing a competing instance.
 *
 * **Where its state lives (T094).** In the app, in the root store under {@link AUTH_SLICE_NAME}
 * (`authSession`, renamed from `auth`, which the legacy token/user reducer keeps), bound through
 * `app-auth-store.ts#bindAppAuthStore`. The root reducer keeps this slice across its logout reset so session
 * generations never restart mid sign-out; the boundary's own cleanup clears it.
 *
 * Lazily constructed on first access (not at module load) so importing this module has no eager
 * side effects, and cached for the lifetime of the page thereafter — the same singleton every
 * caller (this module's own consumers, `AuthRoute.tsx`, `main.tsx`'s boot-time bootstrap,
 * `AuthenticationRouter.tsx`'s `AuthenticationSsoHandoff`) observes.
 */
let appAuthBoundary: AuthBoundary | null = null;

/**
 * Builds the app-wide singleton's real `renewSsoSession` dependency (T027 gap fix; closes the
 * defect where `getAppAuthBoundary()` never wired one at all, so `ensureFreshSession()` always
 * settled `interaction_required` for a real SSO session — see this repo's task-review notes for
 * #3888).
 *
 * `getBoundary` is a thunk rather than a captured value because of construction order:
 * {@link getAppAuthBoundary} must pass this function to `createAuthBoundary` BEFORE the boundary
 * instance itself exists, but the function is only ever actually invoked later (lazily, when
 * `ensureFreshSession()` calls it) — by which point `getAppAuthBoundary()`'s `appAuthBoundary`
 * module variable is already assigned. This mirrors `runSsoBoot`'s `getAuthBoundary` thunk
 * (`sso-boot.ts`) and `dependencies.renewSsoSession` itself already being re-read rather than
 * closed over (see this module's own doc comment on that field).
 *
 * Reuses `getSharedSsoOidcClient` (not a new client) — the same boot-time singleton `main.tsx`
 * constructs unconditionally via `sso-boot.ts`'s `runSsoBoot`. That call is idempotent (returns the
 * already-resolved cached promise once boot has run), so this never performs a second discovery
 * round-trip; it also means the "shared client constructed before login" staleness this task raised
 * does not actually apply here — `oidc-spa`'s redirect URI is always this app's root
 * (`oidc-client.ts`'s `getSharedSsoOidcClient` doc comment), so a real login is always a full-page
 * navigation back to a fresh page load, which re-creates `sharedSsoOidcClientPromise` from scratch
 * with an accurate `isUserLoggedIn`/`renewTokens` for that page's entire lifetime — there is no
 * in-page transition from logged-out to logged-in that could leave the cached client stale.
 *
 * `client.renewTokens` is `undefined` when the resolved client is not in a logged-in state (see
 * `oidc-client.ts`'s `SsoOidcClient.isUserLoggedIn`/`renewTokens` doc comments) — that is reported
 * as a rejection so `ensureFreshSession()`'s existing catch settles it as `interaction_required`,
 * matching every other "renewal unavailable" case.
 */
function createAppRenewSsoSession(getBoundary: () => OidcClientDependencies['authBoundary']): () => Promise<void> {
  return async () => {
    const client = await getSharedSsoOidcClient({ authBoundary: getBoundary() });
    if (!client.isUserLoggedIn || !client.renewTokens) {
      throw new Error('SSO client is not in a logged-in state; silent renewal is unavailable.');
    }
    await client.renewTokens();
  };
}

/**
 * Builds the app-wide singleton's real `logoutSsoSession` dependency (T036), mirroring
 * {@link createAppRenewSsoSession}'s construction-order thunk shape and reuse of
 * `getSharedSsoOidcClient` (the same boot-time singleton, never a second discovery round-trip —
 * see that function's own doc comment for the full rationale, which applies unchanged here).
 *
 * `client.logout` is `undefined` when the resolved client is not in a logged-in state (see
 * `oidc-client.ts`'s `SsoOidcClient.logout` doc comment) — reported as a rejection so
 * `attemptProviderLogout` treats it the same as any other "termination could not be confirmed"
 * case (`PARTIAL_LOGOUT`). In practice `runCleanup()` only calls this dependency at all when an
 * SSO session existed just before local clearing, so this branch is defensive rather than an
 * expected path.
 *
 * `redirectTo: 'home'` is the deliberate choice of post-logout destination (FR-021 "end at one
 * safe unauthenticated destination"): the app's own root, the same fixed redirect URI `oidc-spa`
 * is already configured with for login (`oidc-client.ts`'s module doc comment), so no additional
 * redirect URI needs to be registered with the IdP specifically for logout.
 */
function createAppLogoutSsoSession(
  getBoundary: () => OidcClientDependencies['authBoundary'],
): (postLogoutPath: string) => Promise<void> {
  return async (postLogoutPath) => {
    const client = await getSharedSsoOidcClient({ authBoundary: getBoundary() });
    if (!client.isUserLoggedIn || !client.logout) {
      throw new Error('SSO client is not in a logged-in state; provider logout is unavailable.');
    }
    // oidc-spa still sends the registered app root as `post_logout_redirect_uri` and moves to this
    // path after the provider returns, so no extra IdP registration is needed (T065).
    await client.logout({ redirectTo: 'specific url', url: postLogoutPath });
  };
}

function appAuthControllerStore(): AuthControllerStore {
  const root = getBoundAppAuthStore();
  if (root) {
    return {
      dispatch: root.dispatch,
      getState: () => ({ auth: (root.getState() as Record<string, AuthState>)[AUTH_SLICE_NAME] }),
    };
  }
  // Unit tests that never build the root store get a dedicated one.
  return configureStore({ reducer: { auth: authReducer } }) as unknown as AuthControllerStore;
}

/** Returns the lazily-initialized app-wide {@link AuthBoundary} singleton. See doc comment above. */
export function getAppAuthBoundary(): AuthBoundary {
  if (!appAuthBoundary) {
    const store = appAuthControllerStore();
    const authController = createAuthController(store);
    appAuthBoundary = createAuthBoundary({
      store,
      authController,
      renewSsoSession: createAppRenewSsoSession(() => appAuthBoundary!),
      logoutSsoSession: createAppLogoutSsoSession(() => appAuthBoundary!),
      clearSsoLibraryState: () => {
        const result = validateOidcConfig();
        if (result.valid) markSsoSessionEndedLocally(result.config);
      },
      reportOutcome: (event) => {
        void import('./events').then(({ reportAuthenticationOutcome }) => reportAuthenticationOutcome(event));
      },
    });
  }
  return appAuthBoundary;
}

/**
 * Test-only escape hatch: clears the cached singleton so each test file/case can start from a
 * fresh boundary instead of leaking SSO/session state across tests via this module's cache. Not
 * intended for production call sites.
 */
export function resetAppAuthBoundaryForTesting(): void {
  appAuthBoundary = null;
}
